/**
 * Utilities for handling same-site return URLs between pages (e.g. Build Your Pack -> My Account -> Build Your Pack).
 */

export const RETURN_PARAM = "redirect_to";
const RETURN_STORAGE_KEY = "tek_tides_return_to";
const EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

interface StoredReturnPayload {
  url: string;
  expires: number;
}

/**
 * Sanitizes a candidate return URL to ensure it is strictly a safe, same-origin target.
 * SSR-safe: returns null when window is undefined.
 */
export function sanitizeSameSiteReturnUrl(raw: unknown): string | null {
  if (typeof window === "undefined" || typeof raw !== "string") {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  // Reject backslashes, control characters (ASCII 0-31 and 127), and protocol-relative URLs (//)
  if (trimmed.includes("\\") || /[\x00-\x1F\x7F]/.test(trimmed) || trimmed.startsWith("//")) {
    return null;
  }

  try {
    const parsed = new URL(trimmed, window.location.origin);

    // Protocol must be http: or https: and origin must strictly match window.location.origin
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }

    if (parsed.origin !== window.location.origin) {
      return null;
    }

    const path = parsed.pathname;
    const lowerPath = path.toLowerCase();

    // Reject WordPress admin/login paths
    if (lowerPath.startsWith("/wp-admin") || lowerPath.startsWith("/wp-login.php")) {
      return null;
    }

    // Reject My Account itself to avoid login/redirect loops
    if (lowerPath.includes("/my-account") || lowerPath.includes("/myaccountpage")) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

/**
 * Appends redirect_to=<returnTo> to accountUrl using URL API.
 * Preserves relative vs absolute URL form of accountUrl and any existing search/hash params.
 */
export function buildAccountUrlWithReturn(accountUrl: string, returnTo: string): string {
  const sanitizedReturn = sanitizeSameSiteReturnUrl(returnTo);
  if (!sanitizedReturn) {
    return accountUrl;
  }

  if (typeof window === "undefined") {
    return accountUrl;
  }

  try {
    const isRelative = !accountUrl.startsWith("http://") && !accountUrl.startsWith("https://");
    const parsed = new URL(accountUrl, window.location.origin);
    parsed.searchParams.set(RETURN_PARAM, sanitizedReturn);

    if (isRelative) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.href;
  } catch {
    return accountUrl;
  }
}

/**
 * Persists return URL in sessionStorage with 30-minute expiry.
 * Sanitizes first; ignores invalid.
 */
export function rememberReturnUrl(url: string): void {
  if (typeof window === "undefined") return;
  const sanitized = sanitizeSameSiteReturnUrl(url);
  if (!sanitized) return;

  try {
    const payload: StoredReturnPayload = {
      url: sanitized,
      expires: Date.now() + EXPIRY_MS,
    };
    window.sessionStorage.setItem(RETURN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage might be restricted or full
  }
}

/**
 * Reads remembered return URL from sessionStorage.
 * Validates expiration and sanitizes against current origin. Deletes expired/invalid entries.
 */
export function readRememberedReturnUrl(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(RETURN_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredReturnPayload> | null;
    if (!parsed || typeof parsed.url !== "string" || typeof parsed.expires !== "number") {
      clearRememberedReturnUrl();
      return null;
    }

    if (Date.now() > parsed.expires) {
      clearRememberedReturnUrl();
      return null;
    }

    const sanitized = sanitizeSameSiteReturnUrl(parsed.url);
    if (!sanitized) {
      clearRememberedReturnUrl();
      return null;
    }

    return sanitized;
  } catch {
    return null;
  }
}

/**
 * Clears remembered return URL from sessionStorage.
 */
export function clearRememberedReturnUrl(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(RETURN_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}
